import React, { useState } from 'react'
import classNames from 'classnames'
import { withIdentity, WithIdentityProps } from '#src/containers/Identity'
import AirportTypeaheadInput from '#src/components/AirportTypeaheadInput'
import { Button, DatePicker } from '@deal/components'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { generateCreateUrlFromCategory } from '#src/services/path'
import styles from './styles.css'
import Logger from '#src/services/logger'
import {
  CreateBookingFlightPlanComponent,
  FlightPlanState,
  FlightPlanLegUpsertRequestInput
} from '#src/generated/graphql'
import { RouteSelection } from '#src/typings'

interface JetBookingProps extends RouteComponentProps<{}> {
  sellableHasNoImages?: boolean
  sellableId: string
  categorySlug: string
  categoryId: string
}

const JetBooking: React.SFC<JetBookingProps & WithIdentityProps> = ({
  history,
  sellableHasNoImages,
  myself
}) => {
  const [currentRouteSelection, setCurrentRouteSelection] = useState(RouteSelection.ONE_WAY)
  const [from, handleChangeFromAirport] = useState<string>('')
  const [to, handleChangeToAirport] = useState<string>('')
  const [departDate, handleChangeDepartValue] = useState<Date>()
  const [returnDate, handleChangeReturnValue] = useState<Date>()

  return (
    <CreateBookingFlightPlanComponent
      onCompleted={response => {
        if (
          response &&
          response.createFlightPlan &&
          !response.createFlightPlan.hasError &&
          response.createFlightPlan.flightPlan
        ) {
          history.push(
            generateCreateUrlFromCategory('jet-charters', {
              flightPlanId: response.createFlightPlan.flightPlan.id
            })
          )
        } else {
          history.push(generateCreateUrlFromCategory('jet-charters'))
        }
      }}
      onError={() => {
        Logger.captureException('Error creating flight plan ID on product page')

        history.push(generateCreateUrlFromCategory('jet-charters'))
      }}
    >
      {(createFlightPlanMutation, { loading }) => (
        <div
          className={classNames(styles.container, {
            [styles.containerWithoutImages]: sellableHasNoImages
          })}
        >
          <div className={styles.header}>Request a flight</div>
          <div className={styles.selector}>
            <button
              onClick={() => {
                setCurrentRouteSelection(RouteSelection.ONE_WAY)
              }}
              className={
                currentRouteSelection === RouteSelection.ONE_WAY ? styles.selected : undefined
              }
            >
              One way
            </button>
            <button
              onClick={() => {
                setCurrentRouteSelection(RouteSelection.ROUND_TRIP)
              }}
              className={
                currentRouteSelection === RouteSelection.ROUND_TRIP ? styles.selected : undefined
              }
            >
              Round trip
            </button>
          </div>
          <div className={styles.inputs}>
            <div className={styles.legRow}>
              <div className={styles.airport}>
                <span className={styles.inputLabel}>Flying from</span>
                <AirportTypeaheadInput
                  onSetValue={handleChangeFromAirport}
                  className={styles.airportTypeahead}
                  label=""
                  placeholder="Enter city or airport"
                  labelHidden={true}
                  iconHidden={true}
                />
              </div>
              <div className={styles.date}>
                <span className={styles.inputLabel}>When</span>
                <DatePicker
                  hideOnDayClick={true}
                  styleType="standard"
                  label=""
                  labelHidden={true}
                  iconPlacement="left"
                  selectedDay={departDate}
                  onDayChange={handleChangeDepartValue}
                  placeholder="Depart"
                />
              </div>
            </div>
            <div className={styles.legRow}>
              {currentRouteSelection === RouteSelection.ONE_WAY ? (
                <div className={styles.airportFull}>
                  <span className={styles.inputLabel}>Flying to</span>
                  <AirportTypeaheadInput
                    onSetValue={handleChangeToAirport}
                    className={styles.airportTypeahead}
                    label=""
                    placeholder="Enter city or airport"
                    labelHidden={true}
                    iconHidden={true}
                  />
                </div>
              ) : (
                <>
                  <div className={styles.airport}>
                    <span className={styles.inputLabel}>Flying to</span>
                    <AirportTypeaheadInput
                      onSetValue={handleChangeToAirport}
                      className={styles.airportTypeahead}
                      label=""
                      placeholder="Enter city or airport"
                      labelHidden={true}
                      iconHidden={true}
                    />
                  </div>
                  <div className={styles.date}>
                    <span className={styles.inputLabel}>When</span>
                    <DatePicker
                      hideOnDayClick={true}
                      styleType="standard"
                      label=""
                      iconPlacement="left"
                      selectedDay={returnDate}
                      onDayChange={handleChangeReturnValue}
                      minDate={departDate}
                      labelHidden={true}
                      placeholder="Return"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <Button
            className={styles.requestToBook}
            disabled={loading}
            onClick={() => {
              const leg1: FlightPlanLegUpsertRequestInput[] = [
                {
                  departAt: departDate ? departDate.toISOString() : '',
                  departureAirportId: from,
                  arrivalAirportId: to
                }
              ]
              const leg2: FlightPlanLegUpsertRequestInput[] =
                currentRouteSelection === RouteSelection.ROUND_TRIP
                  ? [
                      {
                        departAt: returnDate ? returnDate.toISOString() : '',
                        departureAirportId: to,
                        arrivalAirportId: from
                      }
                    ]
                  : []
              createFlightPlanMutation({
                variables: {
                  input: {
                    state: FlightPlanState.ACTIVE,
                    legs: [...leg1, ...leg2],
                    consumerId: myself ? myself.id : null
                  }
                }
              })
            }}
          >
            Request to Book
          </Button>
        </div>
      )}
    </CreateBookingFlightPlanComponent>
  )
}

export default withRouter(withIdentity(JetBooking))
